import { Component, OnInit, ViewChild, TemplateRef, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { DocumentManagementService } from '../document-management.service';
import { FileModel } from '../file.model';
import { NgbPopover, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
	selector: 'app-manage-documents',
	templateUrl: './manage-documents.component.html',
	styleUrls: ['./manage-documents.component.css'],
})
export class ManageDocumentsComponent implements OnInit {
	files: FileModel[] = [];
	selectedFile: FileModel | null = null;
	itemToDelete: any | null = null;
	@ViewChild('addFileModal') addFileModal: TemplateRef<any>;
	uploadForm: FormGroup;
	fileToUpload: File = null;
	availableCategories: any[] = [];
	public sortableColumns = [
		{ key: 'filename', label: 'Title' },
		{ key: 'fileType', label: 'Type' },
		{ key: 'fileSize', label: 'Size' },
		{ key: 'description', label: 'Description' },
		{ key: 'created', label: 'Created' },
		{ key: 'updated', label: 'Updated' },
		{ key: 'actions', label: 'Actions' },
	];

	downloadingFile: any;
	loading: any;

	uploading: boolean = false;

	groupedFiles: { category: string; files: FileModel[] }[] = [];

	selectedFiles: FileModel[] = [];
	bulkCategory: string = '';
	@ViewChild('bulkCategoryModal') bulkCategoryModal: TemplateRef<any>;

	newCategory: string = '';

	@ViewChild('manageCategoriesModal') manageCategoriesModal: TemplateRef<any>;
	categoryToDelete: number = null;

	bulkCategoryLoading: boolean = false;
	bulkDeleteLoading: boolean = false;

	constructor(
		private documentService: DocumentManagementService,
		private modalService: NgbModal,
		private formBuilder: FormBuilder
	) {
		this.uploadForm = this.formBuilder.group({
			category: ['', Validators.required],
			description: [''],
			fileName: ['', Validators.required],
		});
	}

	ngOnInit(): void {
		this.loadDocuments(null);
		this.loadCategories();
	}

	loadDocuments(category: any | null): void {
		this.loading = true;
		this.documentService.getFilesByCategory(category).subscribe((data: any) => {
			this.files = data.files;
			this.groupFilesByCategory();
			this.loading = false;
		});
	}

	downloadAllFilesInCategory(categoryId: number): void {
		this.downloadingFile = 'all files';
		this.documentService.downloadAllFilesByCategory(categoryId).subscribe({
			next: (response: Blob) => {
				console.log('Downloading all files:', response);
				const url = window.URL.createObjectURL(response);
				const a = document.createElement('a');
				a.href = url;
				a.download = 'documents.zip'; // Suggested filename
				document.body.appendChild(a);
				a.click();

				// Clean up
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
				this.downloadingFile = null;
			},
			error: (error) => {
				console.error('Error downloading files:', error);
				this.downloadingFile = null;
			},
		});
	}

	loadCategories(): void {
		this.documentService.fetchCategories().subscribe({
			next: (response) => {
				this.availableCategories = response.categories || [];
				console.log('Categories loaded:', this.availableCategories);
			},
			error: (error) => {
				console.error('Failed to load categories', error);
				this.availableCategories = [];
			},
		});
	}

	/**
	 * Group files by their category
	 */
	groupFilesByCategory(): void {
		this.groupedFiles = [];

		const categories = [...new Set(this.files.map((file) => file.category || 'Uncategorized'))];

		categories.forEach((category) => {
			const filesInCategory = this.files.filter((file) => (file.category || 'Uncategorized') === category);

			if (filesInCategory.length > 0) {
				this.groupedFiles.push({
					category: category,
					files: filesInCategory,
				});
			}
		});
	}

	setSelectedFile(file: FileModel): void {
		this.selectedFile = file;
	}

	openDeleteConfirm(file: any, popover: NgbPopover) {
		this.selectedFile = file;
		popover.open();
	}

	deleteFile(): void {
		if (!this.selectedFile) return;

		this.itemToDelete = this.selectedFile.id;

		setTimeout(() => {
			this.documentService.deleteFile(this.selectedFile.id).subscribe(() => {
				this.loadDocuments(null);
				this.itemToDelete = null;
			});
		}, 500);
	}

	openAddFileModal() {
		this.uploadForm.reset();
		this.fileToUpload = null;
		this.newCategory = '';

		this.modalService.open(this.addFileModal, { size: 'lg' });
	}

	onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			this.fileToUpload = input.files[0];
		}
	}

	uploadFile(): void {
		if (!this.fileToUpload || !this.uploadForm.valid) {
			return;
		}

		this.uploading = true;

		this.documentService
			.uploadFile(
				this.fileToUpload,
				this.uploadForm.get('category').value,
				this.uploadForm.get('description').value,
				null,
				this.uploadForm.get('fileName').value
			)
			.subscribe({
				next: (response) => {
					this.uploading = false;
					this.modalService.dismissAll();
					this.loadDocuments(null);
				},
				error: (error) => {
					console.error('Error uploading file:', error);
					this.uploading = false;
				},
			});
	}

	showErrorMessage(message: string) {
		console.log('Error:', message);
	}

	/**
	 * Download a file
	 * @param file The file object to download
	 */
	openFile(file: any): void {
		this.documentService.getFileUrl(file.id).subscribe({
			next: (response: any) => {
				this.downloadingFile = null;
				if (response && response.url) {
					window.open(response.url, '_blank');
				} else {
					console.error('Invalid response format:', response);
					//	this.showErrorMessage('Failed to download file: Invalid response');
				}
			},
			error: (error) => {
				console.error('Error opening file:', error);
			},
		});
	}

	/**
	 * Handles the drop event when a user drags and drops a row within a category
	 */
	drop(event: CdkDragDrop<FileModel[]>, categoryFiles: FileModel[]) {
		if (event.previousContainer === event.container) {
			moveItemInArray(categoryFiles, event.previousIndex, event.currentIndex);

			let position = 1;
			this.groupedFiles.forEach((group) => {
				group.files.forEach((file) => {
					file.orderPosition = position++;
				});
			});

			this.files = this.groupedFiles.flatMap((group) => group.files);

			this.saveOrderPositions();
		}
	}

	/**
	 * Saves the current order positions to the database
	 */
	saveOrderPositions() {
		const orderData = this.files.map((file) => ({
			id: file.id,
			orderPosition: file.orderPosition,
		}));

		this.documentService.updateFileOrder(orderData).subscribe({
			next: () => {
				console.log('Order updated successfully');
			},
			error: (error) => {
				console.error('Error updating file order:', error);
				this.loadDocuments(null);
			},
		});
	}

	/**
	 * Check if a file is currently selected
	 */
	isFileSelected(file: FileModel): boolean {
		return this.selectedFiles.some((f) => f.id === file.id);
	}

	/**
	 * Toggle selection of a single file
	 */
	toggleFileSelection(file: FileModel, event: Event): void {
		const isChecked = (event.target as HTMLInputElement).checked;

		if (isChecked) {
			if (!this.isFileSelected(file)) {
				this.selectedFiles.push(file);
			}
		} else {
			this.selectedFiles = this.selectedFiles.filter((f) => f.id !== file.id);
		}
	}

	/**
	 * Check if all files in a group are selected
	 */
	areAllFilesInGroupSelected(files: FileModel[]): boolean {
		return files.length > 0 && files.every((file) => this.isFileSelected(file));
	}

	/**
	 * Check if some but not all files in a group are selected
	 */
	arePartialFilesInGroupSelected(files: FileModel[]): boolean {
		const selectedCount = files.filter((file) => this.isFileSelected(file)).length;
		return selectedCount > 0 && selectedCount < files.length;
	}

	/**
	 * Toggle selection for an entire group of files
	 */
	toggleGroupSelection(files: FileModel[], event: Event): void {
		const isChecked = (event.target as HTMLInputElement).checked;

		if (isChecked) {
			files.forEach((file) => {
				if (!this.isFileSelected(file)) {
					this.selectedFiles.push(file);
				}
			});
		} else {
			this.selectedFiles = this.selectedFiles.filter((file) => !files.includes(file));
		}
	}

	/**
	 * Clear all selected files
	 */
	clearSelection(): void {
		this.selectedFiles = [];
	}

	/**
	 * Open the bulk category change modal
	 */
	openBulkCategoryModal(): void {
		this.bulkCategory = '';
		this.modalService.open(this.bulkCategoryModal);
	}

	/**
	 * Bulk change category for selected files
	 */
	bulkChangeCategory(): void {
		if (!this.bulkCategory || this.selectedFiles.length === 0) {
			return;
		}

		this.bulkCategoryLoading = true;
		const fileIds = this.selectedFiles.map((file) => file.id);

		this.documentService.bulkUpdateCategory(fileIds, this.bulkCategory).subscribe({
			next: (response) => {
				this.loadDocuments(null);
				this.clearSelection();
				this.bulkCategoryLoading = false;
			},
			error: (error) => {
				console.error('Error updating categories:', error);
				this.showErrorMessage('Failed to update categories');
				this.bulkCategoryLoading = false;
			},
		});
	}

	/**
	 * Bulk delete files
	 */
	bulkDeleteFiles(): void {
		if (this.selectedFiles.length === 0) {
			return;
		}

		this.bulkDeleteLoading = true;
		const fileIds = this.selectedFiles.map((file) => file.id);

		this.documentService.bulkDeleteFiles(fileIds).subscribe({
			next: (response) => {
				this.loadDocuments(null);
				this.clearSelection();
				this.bulkDeleteLoading = false;
			},
			error: (error) => {
				console.error('Error deleting files:', error);
				this.showErrorMessage('Failed to delete files');
				this.bulkDeleteLoading = false;
			},
		});
	}

	/**
	 * Start editing a specific field
	 * @param file The file to edit
	 * @param fieldName The name of the field to edit
	 */
	startEditing(file: FileModel, fieldName: string): void {
		this.files.forEach((f) => {
			f.isEditing = false;
			f.editingField = null;
		});

		file.isEditing = true;
		file.editingField = fieldName;

		const editValueKey = `edit${this.capitalizeFirstLetter(fieldName)}`;
		file[editValueKey] = file[fieldName];

		setTimeout(() => {
			const inputQueryList = this[`${fieldName}Inputs`] as QueryList<ElementRef>;
			if (inputQueryList) {
				const input = inputQueryList.find((input) => true);
				if (input) {
					input.nativeElement.focus();
				}
			}
		});
	}

	/**
	 * Save the edited field
	 * @param file The file being edited
	 * @param fieldName The name of the field being edited
	 */
	saveField(file: FileModel, fieldName: string): void {
		const editValueKey = `edit${this.capitalizeFirstLetter(fieldName)}`;

		if (!file[editValueKey] || file[editValueKey].trim() === '') {
			return;
		}

		if (file[fieldName] !== file[editValueKey]) {
			this.documentService.updateFileField(file.id, fieldName, file[editValueKey]).subscribe({
				next: (response) => {
					file[fieldName] = file[editValueKey];
				},
				error: (error) => {
					console.error(`Error updating ${fieldName}:`, error);
					this.showErrorMessage(`Failed to update ${fieldName}`);
				},
			});
		}

		file.isEditing = false;
		file.editingField = null;
	}

	/**
	 * Cancel editing a field
	 * @param file The file being edited
	 * @param fieldName The name of the field being edited
	 */
	cancelEditing(file: FileModel, fieldName: string): void {
		file.isEditing = false;
		file.editingField = null;
	}

	/**
	 * Helper method to capitalize the first letter of a string
	 */
	private capitalizeFirstLetter(str: string): string {
		return str.charAt(0).toUpperCase() + str.slice(1);
	}

	/**
	 * Adds a new category and updates the category list
	 */
	addNewCategory(): void {
		if (!this.newCategory?.trim()) {
			return;
		}

		if (this.availableCategories.some((c) => c.name === this.newCategory.trim())) {
			this.showErrorMessage('Category already exists');
			return;
		}

		this.documentService.addCategory(this.newCategory.trim()).subscribe({
			next: (response) => {
				if (response && response.category) {
					this.availableCategories.push(response.category);

					this.availableCategories.sort((a, b) => a.name.localeCompare(b.name));

					this.uploadForm.get('category').setValue(response.category.id);
				}

				this.newCategory = '';
			},
			error: (error) => {
				console.error('Error adding category:', error);
				this.showErrorMessage('Failed to add category');
			},
		});
	}

	/**
	 * Opens the modal for managing categories
	 */
	openManageCategoriesModal(): void {
		this.modalService.open(this.manageCategoriesModal, { size: 'lg' });
	}

	/**
	 * Add a new category from the category manager modal
	 */
	addNewCategoryFromManager(): void {
		if (!this.newCategory?.trim()) {
			return;
		}

		if (this.availableCategories.some((c) => c.name === this.newCategory.trim())) {
			this.showErrorMessage('Category already exists');
			return;
		}

		this.documentService.addCategory(this.newCategory.trim()).subscribe({
			next: (response) => {
				if (response && response.category) {
					this.availableCategories.push(response.category);

					this.availableCategories.sort((a, b) => a.name.localeCompare(b.name));
				}

				this.newCategory = '';
			},
			error: (error) => {
				console.error('Error adding category:', error);
				this.showErrorMessage('Failed to add category');
			},
		});
	}

	/**
	 * Delete a category
	 */
	deleteCategory(): void {
		if (!this.categoryToDelete) {
			return;
		}

		this.documentService.deleteCategory(this.categoryToDelete).subscribe({
			next: (response) => {
				this.availableCategories = this.availableCategories.filter((c) => c.id !== this.categoryToDelete);

				if (this.uploadForm.get('category').value === this.categoryToDelete) {
					this.uploadForm.get('category').setValue('');
				}

				this.categoryToDelete = null;

				this.loadDocuments(null);
			},
			error: (error) => {
				console.error('Error deleting category:', error);
				this.showErrorMessage('Failed to delete category');
			},
		});
	}

	/**
	 * Find a category name by its ID
	 * @param id The category ID to look up
	 * @returns The category name or 'Unknown Category'
	 */
	getCategoryNameById(id: number): string {
		const category = this.availableCategories.find((c) => c.id === id);
		return category ? category.name : 'Unknown Category';
	}
}
